"use client"

import Image from "next/image"
import { motion } from "framer-motion"
import { MarqueeBanner, Nav, colors, exposureFont, EarlyAccessModalProvider, useEarlyAccessModal } from "./components"

export default function LandingPageRework() {
  return (
    <EarlyAccessModalProvider>
      <div className={`min-h-screen bg-black overflow-x-hidden font-inter ${exposureFont.variable}`}>
        {/* Scrolling Marquee Banner */}
        <MarqueeBanner backgroundColor={colors.cyan} textColor={colors.black} />

        {/* Hero Section */}
        <HeroSection />

        {/* Features Section with overlapping LOOP HOME text */}
        <FeaturesSection />

        {/* Photo Collage / Keep it in the Loop Section */}
        <KeepInLoopSection />

        {/* Pink Section */}
        <PinkSection />
      </div>
    </EarlyAccessModalProvider>
  )
}

function HeroSection() {
  const { openModal } = useEarlyAccessModal()
  
  return (
    <section className="relative">
      {/* Navigation - Black bar with cyan logo */}
      <Nav
        logoColor="cyan"
        backgroundColor={colors.black}
        textColor="white"
        earlyAccessStyle="button"
        earlyAccessButtonColor={colors.cyan}
        earlyAccessTextColor={colors.black}
        onEarlyAccessClick={openModal}
      />

      {/* Hero Content - Full width image with text overlay */}
      <div className="relative min-h-[500px] md:min-h-[600px] lg:min-h-[490px]">
        {/* Full Width Hero Image */}
        <div className="absolute inset-0">
          <Image
            src="/landing/hero-sofa.jpg"
            alt="Person relaxing on mustard velvet sofa"
            fill
            className="object-cover object-bottom"
            priority
          />
        </div>

        {/* Hero Text Overlay */}
        <div className="relative z-10 h-full flex items-center">
          <div className="px-6 lg:px-16 py-12 md:py-16 lg:py-24 max-w-md lg:max-w-2xl">
            <h1
              className="text-3xl md:text-4xl lg:text-5xl leading-[1.3] mb-8 text-black"
              style={{ fontFamily: "var(--font-exposure), Georgia, serif" }}
            >
              The pre-loved <br /> 
              furniture marketplace <br />
              that does the hunting<br />
              for you
            </h1>

            {/* CTA Button - cyan bg, black text, pill shape */}
            <button
              onClick={openModal}
              className="inline-block px-12 py-3 text-base font-bold tracking-wide text-black rounded-full transition-all hover:scale-105 hover:shadow-lg uppercase"
              style={{ backgroundColor: colors.cyan }}
            >
              Early Access
            </button>
          </div>
        </div>

        {/* Large LOOP HOME Text - Full width edge to edge */}
        <div className="absolute bottom-0 left-0 right-0 translate-y-[50%] md:translate-y-[70%] z-20 pointer-events-none w-full flex justify-center">
          <Image
            src="/landing/loop-home-text-large.png"
            alt=""
            width={1920}
            height={200}
            className="w-full h-auto max-w-[1200px]"
          />
        </div>
      </div>
    </section>
  )
}

function FeaturesSection() {
  const features = [
    {
      icon: "/landing/icon-room.png",
      title: "Give us your vision",
      description: "We'll hunt across the web for the best pre-loved bits!",
    },
    {
      icon: "/landing/heart-black-png.png",
      title: "Tailor as you go",
      description: "Heart and chat until you find your perfect piece.",
    },
    {
      icon: "/landing/icon-lhe-logo.png",
      title: "Keep it in the Loop",
      description: "When your space or life changes, just Loop it!",
    },
  ]

  return (
    <section
      className="relative pt-40 md:pt-56 lg:pt-64 pb-16 lg:pb-24"
      style={{ backgroundColor: colors.cyan }}
    >
      {/* Watermark Pattern */}
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage: "url(/landing/watermark-pattern.png)",
          backgroundSize: "80% auto",
          backgroundPosition: "center",
          backgroundRepeat: "repeat",
        }}
      />

      {/* Features Card - Sharp corners (no rounded) */}
      <div
        className="relative mx-6 lg:mx-auto max-w-5xl px-6 md:px-12 lg:px-16 py-10 lg:py-14"
        style={{ backgroundColor: colors.cream }}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-12">
          {features.map((feature, index) => (
            <div key={index} className="text-center flex flex-col items-center">
              <div className="h-20 w-20 flex items-end justify-center mb-6">
                <Image
                  src={feature.icon}
                  alt=""
                  width={80}
                  height={80}
                  className="max-h-20 max-w-20 w-auto h-auto object-contain"
                />
              </div>
              <h3
                className="text-xl lg:text-2xl mb-3 text-black min-h-[2rem]"
                style={{ fontFamily: "var(--font-exposure), Georgia, serif", fontWeight: 300 }}
              >
                {feature.title}
              </h3>
              <p className="text-sm text-black/70 leading-relaxed max-w-[200px]">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function KeepInLoopSection() {
  const { openModal } = useEarlyAccessModal()
  
  // TRUE RADIAL ARC: Images on rays from a circle center
  // Desktop: 7 images, 180° arc split into 6 equal 30° segments
  // Mobile: 5 images (hide ±60° middle tier), tighter radius
  
  const images = [
    { src: "/landing/collage-10.png", hideOnMobile: true },   // Ray 0: -90° (hidden on mobile)
    { src: "/landing/collage-11.jpg", hideOnMobile: false },  // Ray 1: -60°
    { src: "/landing/collage-12.jpg", hideOnMobile: false },  // Ray 2: -30°
    { src: "/landing/collage-13.png", hideOnMobile: false },  // Ray 3:   0° (top center)
    { src: "/landing/collage-14.png", hideOnMobile: false },  // Ray 4: +30°
    { src: "/landing/collage-15.png", hideOnMobile: false },  // Ray 5: +60°
    { src: "/landing/collage-16.png", hideOnMobile: true },   // Ray 6: +90° (hidden on mobile)
  ]
  
  // Calculate positions on rays emanating from center
  // Radius is controlled via CSS custom property for responsiveness
  const imageConfig = images.map((img, index) => {
    // 7 images = 6 gaps, 180° / 6 = 30° per gap
    const angleDeg = -90 + (index * 30)
    const angleRad = (angleDeg * Math.PI) / 180
    
    // Normalized position multipliers (will be scaled by CSS --radius)
    const xMultiplier = Math.sin(angleRad)  // -1 to +1
    const yMultiplier = 1 - Math.cos(angleRad)  // 0 to 2
    
    return { 
      src: img.src, 
      hideOnMobile: img.hideOnMobile,
      xMultiplier, 
      yMultiplier, 
      rotation: angleDeg 
    }
  })

  // Pre-calculate positions for mobile and desktop
  // Increased radii so outermost images go slightly off-screen
  const mobileRadius = 38   // ±60° images will clip at edges
  const desktopRadius = 40  // ±90° images will clip at edges
  
  const positions = imageConfig.map((img) => ({
    ...img,
    mobile: {
      x: mobileRadius * img.xMultiplier,
      y: 10 + (mobileRadius * img.yMultiplier * 0.9),  // Flatter arc on mobile
    },
    desktop: {
      x: desktopRadius * img.xMultiplier,
      y: 5 + (desktopRadius * img.yMultiplier * 1.6),
    },
  }))

  return (
    <section className="relative bg-black overflow-x-clip">
      <div className="relative w-full min-h-[480px] md:min-h-[620px] lg:min-h-[700px] max-w-[1800px] mx-auto">
        {/* Collage images - positioned on radial arc */}
        {positions.map((img, index) => (
          <div key={index} className="contents">
            {/* Mobile version */}
            <div
              className={`absolute w-[90px] md:hidden ${
                img.hideOnMobile ? 'hidden' : ''
              }`}
              style={{
                top: `${img.mobile.y}%`,
                left: `calc(50% + ${img.mobile.x}vw)`,
                transform: 'translateX(-50%)',
              }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.8, rotate: img.rotation }}
                whileInView={{ opacity: 1, scale: 1, rotate: img.rotation }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05, duration: 0.4 }}
                style={{ transformOrigin: 'center center' }}
              >
                <div className="rounded-xl overflow-hidden shadow-2xl">
                  <Image
                    src={img.src}
                    alt=""
                    width={234}
                    height={264}
                    className="w-full h-auto object-cover"
                    style={{ aspectRatio: '234/264' }}
                  />
                </div>
              </motion.div>
            </div>
            
            {/* Desktop version */}
            <div
              className="absolute hidden md:block md:w-[140px] lg:w-[180px]"
              style={{
                top: `${img.desktop.y}%`,
                left: `calc(50% + ${img.desktop.x}vw)`,
                transform: 'translateX(-50%)',
              }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.8, rotate: img.rotation }}
                whileInView={{ opacity: 1, scale: 1, rotate: img.rotation }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05, duration: 0.4 }}
                style={{ transformOrigin: 'center center' }}
              >
                <div className="rounded-xl lg:rounded-2xl overflow-hidden shadow-2xl">
                  <Image
                    src={img.src}
                    alt=""
                    width={234}
                    height={264}
                    className="w-full h-auto object-cover"
                    style={{ aspectRatio: '234/264' }}
                  />
                </div>
              </motion.div>
            </div>
          </div>
        ))}

        {/* Center Text - positioned lower to sit between images and button */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 z-10 pt-48 md:pt-28 lg:pt-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="overflow-hidden"
          >
            <Image
              src="/images/keep-it-in-the-loop.svg"
              alt="Keep it in the LOOP"
              width={435}
              height={247}
              className="w-[280px] md:w-[380px] lg:w-[435px] h-auto mb-[-30px] md:mb-[-40px] lg:mb-[-50px]"
            />
          </motion.div>

          {/* Get Access Button - consistent size */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <button
              onClick={openModal}
              className="mt-4 md:mt-5 inline-block px-12 py-3 text-sm md:text-base font-bold tracking-wide text-black rounded-full transition-all hover:scale-105 uppercase"
              style={{ backgroundColor: colors.cream }}
            >
              Early Access
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

function PinkSection() {
  const { openModal } = useEarlyAccessModal()
  
  return (
    <section 
      className="relative py-16 md:py-24 md:-mt-24 lg:-mt-32 z-10" 
      style={{ backgroundColor: colors.pink }}
    >
      {/* Watermark Pattern */}
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage: "url(/landing/watermark-pattern.png)",
          backgroundSize: "200% auto",
          backgroundPosition: "center",
          backgroundRepeat: "repeat",
        }}
      />

      <div className="relative max-w-6xl mx-auto px-6 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl leading-tight mb-6 text-black"
              style={{ fontFamily: "var(--font-exposure), Georgia, serif" }}
            >
              Loop Home is for people who build identity and joy in the things
              they find.
            </h2>

            <p className="text-lg md:text-xl lg:text-2xl mb-8 text-black/80 leading-relaxed">
              It's for a generation of people who express themselves through finding pre-loved furniture that is unique to them.
              <br /><br />
              In a world where forever homes are not for everyone, we are building homes that evolve with us and our squiggly lives.
            </p>

            {/* CTA Button - cream bg, black text, pill */}
            <button
              onClick={openModal}
              className="inline-block px-12 py-3 text-base font-bold tracking-wide text-black rounded-full transition-all hover:scale-105 uppercase"
              style={{ backgroundColor: colors.cream }}
            >
              Early Access
            </button>
          </div>

          {/* Right Image */}
          <div className="flex justify-center lg:justify-end">
            <Image
              src="/images/thrill-of-the-find.png"
              alt="The Thrill of the Find"
              width={462}
              height={614}
              className="w-full max-w-sm lg:max-w-md h-auto"
            />
          </div>
        </div>
      </div>
    </section>
  )
}
